/**
 * Near-field animated ocean (Layer-1 geometry for the sea). The per-planet sea
 * is a single static sphere — fine for the far horizon, dead flat underfoot.
 * This drops a high-resolution grid under the viewer, projects it onto the sea
 * sphere, displaces it with the shared Gerstner core (gerstner.ts) and shades
 * it with the shared water look (waterMaterial.ts). Its rim fades (amplitude →
 * flat, alpha → 0) so it dissolves into the far sphere with no visible seam.
 *
 * The surface uses DEPTH: we cache the analytic terrain floor under the grid footprint (the engine's
 * storage-free `PlanetSampler.surfaceRadius`) into a small DataTexture, so the
 * surface knows how deep the water is and can colour-absorb, shoal its waves,
 * and foam at the shoreline — entirely on the CPU, with no depth buffer and no
 * logarithmic-depth reconstruction (the locked "analytic terrain-driven"
 * decision).
 *
 * Everything is computed in render space (origin-relative) and with the planet's
 * INTERPOLATED render transform, so the math stays float32-stable and the water
 * never judders against the far sphere or the avatar.
 */
import * as THREE from "three";
import { CLOUD_MAPPING_GLSL } from "../weather/cloudMapping";
import type { Planet } from "../planet";
import { gerstnerGLSL } from "./gerstner";
import { COASTAL_DEPTH_GLSL, COASTAL_SURF_GLSL, SHORE_SPAN, SHORE_RES, SHORE_STEP, SHORE_CACHE_STEP, fillShoreWindow } from "./coastalSurf";
import { WATER_CHARACTER_GLSL } from "./surfaceCharacter";
import { sharedWaterUniforms, deriveWaterColors, WATER_UNIFORMS_GLSL, WATER_SHADE_GLSL } from "./waterMaterial";
import { WaterReflection } from "./waterReflection";
import { ShoreRockField, SHORE_ROCK_GLSL } from './shoreRockField';
import { fillDepthWindow } from './depthWindow';

/** Patch span (metres) and grid resolution. Sized large so the animated water reaches
 *  well out across the big (WORLD_SCALE'd) worlds before dissolving into the far sphere.
 *  The grid is GRADED (see INNER_HALF), so far fewer segments cover the same span than
 *  the old uniform 640² mesh did — resolution goes where the pixels are. */
const PATCH_SIZE = 2400;
const PATCH_SEGS = 256;
/** Central 160 m uses uniform 1.25 m cells to resolve steep crest faces.
 * The outer grid expands quadratically to the 2.4 km horizon footprint.
 * Topology stays at 131k triangles; normals still resolve per fragment. */
const INNER_HALF = 80; // 1.25 m cells over the central 160 m; same 131k triangles
/** Coarse, cached depth for distant water colour. A separate signed 2 m grid
 * resolves the nearby shoreline and breaking surf. */
const DEPTH_RES = 96;
const DEPTH_N = DEPTH_RES + 1;
/** Metres per depth texel — also the step of the world lattice the bake snaps to. */
const DEPTH_TEXEL = PATCH_SIZE / DEPTH_RES;
/** Depth range (metres) packed into the 8-bit depth texture. */
const DEPTH_STORE_MAX = 24;
/** Re-anchor the gnomonic centre once the viewer drifts ~10° (keeps it exact). */
const REANCHOR_DOT = 0.985;

/** Whether fillDepth must re-run this frame. Pure so it can be unit-tested
 *  (client/test/_oceandepth.mts) without a Planet/WebGL. `cellChanged` is true once
 *  the viewer has walked into a new cell of the bake lattice — see update(). */
export function depthRebakeNeeded(
  baked: boolean,
  reanchored: boolean,
  planetChanged: boolean,
  cellChanged: boolean,
): boolean {
  return !baked || reanchored || planetChanged || cellChanged;
}
/** Wrap the wave clock to keep shader phase in good float32 range. */
const TIME_WRAP = 600;
/** Hide the patch outside this altitude band over the sea (far sphere covers). Raised so the
 *  now-larger patch keeps showing animated water from a bit higher up. */
const SHOW_ALT_MAX = 700;
const SHOW_ALT_MIN = -60;
/** Planar-reflection strength mixed into the grazing reflection (Fresnel still gates it). */
const REFL_STRENGTH = 1.0;
/** Render the mirror through a frustum this much wider than the camera's. A point on
 *  the water plane projects to the same pixel through the mirrored camera as through
 *  the real one, so at 1.0 the reflection texture's border lands exactly on the
 *  screen's border and the sampler's edge fade paints a bright frame around the
 *  viewport. The margin puts that border safely off-screen. See WaterReflection.render. */
const REFL_FOV_SCALE = 1.24;

const UP_Y = new THREE.Vector3(0, 1, 0);
const UP_X = new THREE.Vector3(1, 0, 0);
/** 1×1 placeholder for the cloud-cover sampler when the sea planet has no clouds. */
const CLOUD_DUMMY = new THREE.DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1);
CLOUD_DUMMY.needsUpdate = true;

export class OceanPatch {
  readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;
  private readonly refLocal = new THREE.Vector3();
  private anchored = false;

  // Planar reflection: a half-res mirror of the scene across the patch's tangent
  // plane, sampled projectively in the shader (the `sky` hook). Rendered by the
  // caller each frame via renderReflection() when the patch is on screen.
  private readonly reflection = new WaterReflection(512, 256);
  private readonly riverPlane = new THREE.Plane();
  private readonly hidden: THREE.Object3D[] = [];
  private readonly hiddenVis: boolean[] = [];
  /** Refresh the mirror every Nth call (1 = every frame). The mirror pass re-renders
   *  the whole scene, which on this fill-bound client is the ocean's dominant GPU
   *  cost — and the texture persists between refreshes, so at 2 the wave-slope
   *  ripple (uReflDistort) hides the one-frame lag completely. Live knob:
   *  `__hl.ocean().reflInterval = 1` for the A/B. */
  reflInterval = 2;
  /** Camera jump (metres, render space, one frame) that forces a mirror refresh —
   *  teleports, warps, floating-origin rebases. Live so probes can hold it open:
   *  headless frames are seconds long and orbital carry alone exceeds 50 m/frame. */
  reflJumpM = 50;
  /** Planet-relative EYE travel (metres since the last mirror render) beyond which
   *  the mirror refreshes instead of being reused. The pose re-aim makes a stale
   *  mirror exact under WORLD motion, but eye translation changes the reflected
   *  image by PARALLAX — a stale texture cannot contain the new viewpoint, and at
   *  the shoreline (reflected bank objects metres away) a sprint's 0.2 m/frame
   *  read as a 30 Hz jitter. Rotation costs nothing (projective sampling is
   *  orientation-invariant for a fixed eye), so half-rate survives look-around and
   *  idling — it only yields to actual translation. Live knob, like the others. */
  reflEyeMoveM = 0.02;
  private reflFrame = 0;
  private mirrorFresh = false;
  private readonly lastReflCam = new THREE.Vector3();
  private readonly camNow = new THREE.Vector3();
  // Planet pose (render space) this frame and at the last mirror render — the
  // difference re-aims the stale mirror matrix on reuse frames. Without it the
  // planet's orbit+spin translate every render-space coordinate each frame while
  // the stale matrix still projects from the OLD poses, so the reflection landed
  // one frame of orbital motion off on every other frame: a 30 Hz jitter.
  private readonly poseNowPos = new THREE.Vector3();
  private readonly poseNowQuat = new THREE.Quaternion();
  private readonly mirrorPos = new THREE.Vector3();
  private readonly mirrorQuat = new THREE.Quaternion();
  private readonly reaimA = new THREE.Matrix4();
  private readonly reaimB = new THREE.Matrix4();
  // Eye position in the PLANET frame at the last mirror render vs now — the
  // parallax gate (reflEyeMoveM) compares these, so pure carry motion (planet
  // dragging both eye and world) cancels out and doesn't force refreshes.
  private readonly mirrorEyeLocal = new THREE.Vector3();
  private readonly eyeLocalNow = new THREE.Vector3();
  private readonly spinInvNow = new THREE.Quaternion();

  // Analytic-depth texture: floor depth sampled under the grid footprint.
  private readonly depthData = new Uint8Array(DEPTH_N * DEPTH_N * 4);
  private readonly depthScratch = new Uint8Array(DEPTH_N * DEPTH_N * 4);
  readonly depthStats = { bakes: 0, sampled: 0, reused: 0, milliseconds: 0 };
  private readonly depthTex: THREE.DataTexture;
  private readonly shoreData = new Uint16Array((SHORE_RES+1)**2*2);
  private readonly shoreScratch = new Uint16Array((SHORE_RES+1)**2*2);
  private readonly shoreSample = {h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8,estuaryShelter:0};
  readonly shoreStats = { bakes: 0, sampled: 0, reused: 0, milliseconds: 0 };
  private readonly shoreTex: THREE.DataTexture;
  readonly shoreRocks = new ShoreRockField();
  private shoreX = Infinity;
  private shoreY = Infinity;
  private shorePlanet: Planet | null = null;
  private lastSeaKey = "";
  // Depth-bake cache: last-baked planet + the lattice cell the bake is centred on
  // (tangent metres from the anchor, always a whole multiple of DEPTH_TEXEL), so a
  // stationary viewer bakes the floor field once and a walking one only on a cell
  // crossing. See the snapping note in update().
  private depthBaked = false;
  private lastBakePlanet: Planet | null = null;
  private bakeX = 0;
  private bakeY = 0;

  // scratch (no per-frame allocation)
  private readonly up = new THREE.Vector3();
  private readonly localDir = new THREE.Vector3();
  private readonly eLocal = new THREE.Vector3();
  private readonly nLocal = new THREE.Vector3();
  private readonly eRen = new THREE.Vector3();
  private readonly nRen = new THREE.Vector3();
  private readonly refRen = new THREE.Vector3();
  private readonly center = new THREE.Vector3();
  private readonly node = new THREE.Vector3();
  private readonly basis = new THREE.Matrix4();
  private readonly invSpin = new THREE.Quaternion();
  private readonly cloudM4 = new THREE.Matrix4(); // invSpin → mat3 scratch for the cloud-shadow rotate

  constructor() {
    this.depthTex = new THREE.DataTexture(this.depthData, DEPTH_N, DEPTH_N, THREE.RGBAFormat);
    this.depthTex.minFilter = THREE.LinearFilter;
    this.depthTex.magFilter = THREE.LinearFilter;
    this.depthTex.wrapS = THREE.ClampToEdgeWrapping;
    this.depthTex.wrapT = THREE.ClampToEdgeWrapping;
    this.depthTex.generateMipmaps = false;
    this.depthTex.needsUpdate = true;

    this.shoreTex = new THREE.DataTexture(this.shoreData,SHORE_RES+1,SHORE_RES+1,THREE.RGFormat,THREE.HalfFloatType);
    this.shoreTex.minFilter = this.shoreTex.magFilter = THREE.LinearFilter;
    this.shoreTex.generateMipmaps = false;
    this.shoreTex.needsUpdate = true;

    const geo = new THREE.PlaneGeometry(PATCH_SIZE, PATCH_SIZE, PATCH_SEGS, PATCH_SEGS);
    // Grade the grid toward the centre (see INNER_HALF). Per-axis, in place: the
    // topology (and the shared-vertex indexing — no cracks) is untouched, endpoints
    // map to themselves exactly, and everything downstream keys off the warped
    // position.xy, so the edge fade and the sphere projection need no changes.
    {
      const pos = geo.attributes.position as THREE.BufferAttribute;
      const half = PATCH_SIZE * 0.5;
      const warp = (v: number): number => {
        const u=Math.abs(v)/half;
        const q=Math.max(0,(u-.5)*2);
        // Derivatives match at the inner/outer join. Spend vertices on the
        // crest silhouettes; fragment normals already handle the far sea.
        return Math.sign(v)*(u<=.5?u*INNER_HALF*2:INNER_HALF+INNER_HALF*q+(half-2*INNER_HALF)*q*q);
      };
      for (let i = 0; i < pos.count; i++) pos.setXY(i, warp(pos.getX(i)), warp(pos.getY(i)));
    }
    this.mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      fog: true,
      uniforms: Object.assign(
        THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
        sharedWaterUniforms([0.1, 0.3, 0.45]),
        {
          uTime: { value: 0 },
          uCenter: { value: new THREE.Vector3() },
          uSeaR: { value: 1 },
          uRefDir: { value: new THREE.Vector3(0, 0, 1) },
          uE: { value: new THREE.Vector3(1, 0, 0) },
          uN: { value: new THREE.Vector3(0, 1, 0) },
          uHalf: { value: PATCH_SIZE * 0.5 },
          uDepthTex: { value: this.depthTex },
          uStoreMax: { value: DEPTH_STORE_MAX },
          uShoreDepth: { value: this.shoreTex },
          uShoreCenter: { value: new THREE.Vector2() },
          uShoreView: { value: new THREE.Vector2() },
          uShoreReady: { value: 0 },
          uShoreRocks: { value: this.shoreRocks.texture },
          uRockCenter: { value: this.shoreRocks.center },
          uRockReady: { value: 0 },
          // Centre of the baked depth field, in the anchored gnomonic coords the
          // wave plane already uses (metres). See the lattice note in update().
          uDepthCenter: { value: new THREE.Vector2() },
          // Planar reflection (set by renderReflection; strength 0 ⇒ fog-sky fallback).
          // uReflMatrix is the patch's OWN matrix, not an alias of the probe's: on
          // mirror-reuse frames it is the probe matrix RE-AIMED by the planet's
          // motion since the mirror was rendered (see renderReflection).
          uReflTex: { value: this.reflection.texture },
          uReflMatrix: { value: new THREE.Matrix4() },
          uReflViewMatrix: { value: new THREE.Matrix4() },
          uPatchOrigin: { value: new THREE.Vector3() },
          uReflStrength: { value: 0 },
          uReflDistort: { value: 0.045 }, // wave-slope ripple on the reflected image
          uReflDebug: { value: 0 }, // 1 ⇒ output the raw reflection (diagnostic)
          // Cloud shadows (set from the sea planet's low deck each frame; str 0 ⇒ off).
          uCloudCover: { value: CLOUD_DUMMY as THREE.Texture },
          uCloudFrame: { value: new THREE.Matrix3() },
          uCloudR: { value: 1 },
          uCloudShadowStr: { value: 0 },
          uCloudInvRot: { value: new THREE.Matrix3() },
          uCloudSun: { value: new THREE.Vector3(0, 1, 0) },
        },
      ),
      vertexShader: /* glsl */ `
        #include <common>
        #include <logdepthbuf_pars_vertex>
        #include <fog_pars_vertex>
        uniform float uTime;
        uniform vec3 uCenter;
        uniform float uSeaR;
        uniform vec3 uRefDir;
        uniform vec3 uE;
        uniform vec3 uN;
        uniform vec3 uPatchOrigin;
        uniform mat4 uReflViewMatrix;
        uniform float uHalf;
        uniform float uAmp;
        uniform sampler2D uDepthTex;
        uniform float uStoreMax;
        uniform vec2 uDepthCenter;
        varying vec3 vWorld;
        varying vec3 vView;
        varying vec4 vReflection;
        varying vec3 vUp;
        varying vec2 vP2;
        varying float vEdge;
        varying float vAmp;
        varying float vDepth;
        ${gerstnerGLSL()}
        ${COASTAL_DEPTH_GLSL}
        ${COASTAL_SURF_GLSL}
        ${SHORE_ROCK_GLSL}
        void main() {
          // Small planet-relative coordinates preserve sub-metre waves even
          // when the render origin is millions of metres from this planet.
          vec3 rel = mat3(modelMatrix) * position + uPatchOrigin;
          vec3 up = normalize(rel);
          vec3 sphereRel = up * uSeaR;
          float denom = max(dot(up, uRefDir), 0.15);
          vec2 p2 = uSeaR * vec2(dot(up, uE), dot(up, uN)) / denom;
          float edge = 1.0 - smoothstep(uHalf * 0.62, uHalf * 0.97, length(position.xy));
          // Depth is indexed by p2 — the ANCHORED gnomonic coordinate — not by the
          // mesh's own uv, which is centred on the viewer and so slides every frame.
          // p2 is a fixed function of position on the planet, and the bake centre is
          // snapped to whole texels of it, so the depth a given piece of seabed reads
          // is identical before and after a rebake. Indexing by uv instead put the
          // texel grid on the viewer, and bilinear reconstruction from 25 m texels
          // then shifted the shoreline every time the cache expired.
          // The stored value is sqrt-encoded so the 8 bits crowd into the shallows.
          vec2 dUv = (p2 - uDepthCenter) / (uHalf * 2.0) + 0.5;
          vec4 depthSample = texture2D(uDepthTex, dUv);
          float dEnc = depthSample.r;
          float depth = mix(dEnc*dEnc*uStoreMax,shoreDepth(p2),shoreWeight(p2));
          // Waves flatten as they run into the shallows (and at the rim).
          float exposure=1.0-0.93*estuaryShelter(p2,depthSample.g);
          float amp = edge * smoothstep(0.0, 3.0, depth) * uAmp * exposure;
          vec3 nrm;
          vec3 disp = seaGerstner(p2, uTime, amp, nrm); // disp already amp-scaled
          vec2 gradient=shoreGradient(p2);
          float grade=length(gradient),shoreK=shoreWeight(p2);
          vec4 runup=beachRunup(p2,grade,uTime);
          runup.w*=exposure;runup.xy*=exposure;
          vec3 breaker=coastalSurf(p2,depth,grade,uTime);
          vec4 rock=vec4(8.0,0.0,0.0,0.0);
          if(shoreK>0.001&&depth>-0.9&&depth<4.4)rock=rockResponse(p2,gradient,max(breaker.x,max(0.0,-depth)+0.06));
          breaker=coastalSurf(p2,depth-0.28*rock.w,grade,uTime)*exposure;
          float surf=breaker.x*(1.0-0.75*rock.z)*shoreK*edge*uAmp;
          // The thin swash follows the signed terrain height above sea level.
          // The ordinary sea geometry joins it smoothly below the waterline.
          float film=(max(0.0,-depth)+0.055+0.035*smoothstep(0.0,0.1,depth+runup.x));
          float filmK=(1.0-smoothstep(0.05,0.5,depth))*runup.w*shoreK*edge;
          surf=mix(surf,max(surf,film),filmK);
          surf+=0.12*exp(-max(0.0,rock.x)*2.8)*rock.y*breaker.y*shoreK;
          vec3 offset = sphereRel-rel + up*(disp.z+surf) + uE*disp.x + uN*disp.y;
          vec3 worldPos = uCenter + rel + offset;
          // The wave NORMAL is recomputed per-fragment (below) — only the wave-plane
          // coords + amplitude are interpolated, so a coarse mesh shades smoothly.
          vWorld = worldPos;
          vUp = up;
          vP2 = p2;
          vEdge = edge;
          vAmp = amp;
          vDepth = depth;
          vec4 mv = modelViewMatrix * vec4(position,1.0) + viewMatrix * vec4(offset,0.0);
          vView=mv.xyz;
          vReflection=uReflViewMatrix*mv;
          gl_Position = projectionMatrix * mv;
          #ifdef USE_FOG
            vFogDepth = -mv.z;
          #endif
          #include <logdepthbuf_vertex>
        }
      `,
      fragmentShader: /* glsl */ `
        #include <common>
        #include <logdepthbuf_pars_fragment>
        #include <fog_pars_fragment>
        ${WATER_UNIFORMS_GLSL}
        ${WATER_SHADE_GLSL}
        ${WATER_CHARACTER_GLSL}
        uniform float uTime;
        uniform vec3 uE;
        uniform vec3 uN;
        uniform sampler2D uReflTex;
        uniform sampler2D uDepthTex;
        uniform vec2 uDepthCenter;
        uniform float uHalf;
        uniform float uReflStrength;
        uniform float uReflDistort;
        uniform float uReflDebug;
        uniform vec3 uCenter;
        ${CLOUD_MAPPING_GLSL}
        uniform sampler2D uCloudCover;
        uniform mat3 uCloudFrame;
        uniform float uCloudR;
        uniform float uCloudShadowStr;
        uniform mat3 uCloudInvRot;
        uniform vec3 uCloudSun;
        // Same cloud shadow as the terrain, but the water is in WORLD space — fold it into
        // the planet-local frame first, then climb toward the sun to the deck + sample cover.
        float oceanCloudShadow(vec3 wpos) {
          if (uCloudShadowStr <= 0.0) return 1.0;
          vec3 P = uCloudInvRot * (wpos - uCenter);
          float bb = dot(P, uCloudSun);
          float disc = bb * bb - (dot(P, P) - uCloudR * uCloudR);
          if (disc <= 0.0) return 1.0;
          vec3 cd = normalize(P + uCloudSun * (-bb + sqrt(disc)));
          vec2 cuv = cloudUv(uCloudFrame * cd);
          float cov = texture2D(uCloudCover, cuv).a;
          float sunUp = smoothstep(0.0, 0.25, dot(uCloudSun, normalize(P)));
          return 1.0 - cov * uCloudShadowStr * sunUp;
        }
        varying vec3 vWorld;
        varying vec3 vView;
        varying vec4 vReflection;
        varying vec3 vUp;
        varying vec2 vP2;
        varying float vEdge;
        varying float vAmp;
        varying float vDepth;
        ${gerstnerGLSL()}
        ${COASTAL_DEPTH_GLSL}
        ${COASTAL_SURF_GLSL}
        ${SHORE_ROCK_GLSL}
        // Broken cell edges give the draining sheet fine branching lace.
        // This is fragment-only and evaluated inside the narrow wash strip.
        vec2 foamSeed(vec2 p){
          vec3 q=fract(vec3(p.xyx)*vec3(0.1031,0.1030,0.0973));
          q+=dot(q,q.yzx+33.33);
          return fract((q.xx+q.yz)*q.zy);
        }
        float foamLace(vec2 p){
          p+=0.22*vec2(sin(p.y*2.7+p.x*0.6),sin(p.x*2.3-p.y*0.8));
          vec2 cell=floor(p),q=fract(p);float first=8.0,second=8.0;
          for(int y=-1;y<=1;y++)for(int x=-1;x<=1;x++){
            vec2 g=vec2(float(x),float(y));
            vec2 v=g+0.18+0.64*foamSeed(cell+g)-q;
            float d=dot(v,v);
            if(d<first){second=first;first=d;}else second=min(second,d);
          }
          return (1.0-smoothstep(0.012,0.055,second-first))
            *smoothstep(0.05,0.5,sin(p.x*0.61+p.y*0.35)+0.4*sin(p.x*1.31-p.y*0.53));
        }
        void main() {
          #include <logdepthbuf_fragment>
          // Per-fragment wave normal: re-evaluate the Gerstner sum at this pixel
          // so the large, coarse patch shades smoothly (no grid-aliased glint web
          // from interpolated vertex normals) — but only where the pixel can still
          // RESOLVE a wave. Out near the horizon one pixel spans tens of metres of
          // wave plane, and a point-sampled 7-wave sum at that rate is noise, not
          // swell: it boils frame to frame and the sharp (exponent-95) sun glint
          // turns it into a band of white sparkle. Measure the pixel's footprint in
          // the wave plane and fade the slope out as it outruns the waves — and once
          // it has, SKIP the 7 sin/cos pairs entirely. The branch is screen-coherent
          // (a horizon band), so the far half of a sea view pays nothing.
          vec2 fw = fwidth(vP2);
          float detail = 1.0 - smoothstep(4.0, 30.0, max(fw.x, fw.y));
          float shoreK=shoreWeight(vP2);
          float depth=mix(vDepth,shoreDepth(vP2),shoreK);
          vec2 gradient=shoreGradient(vP2);
          float grade=length(gradient);
          vec4 runup=beachRunup(vP2,grade,uTime);
          float exposure=1.0-0.93*estuaryShelter(vP2,texture2D(uDepthTex,(vP2-uDepthCenter)/(uHalf*2.0)+0.5).g);
          runup.w*=exposure;runup.xy*=exposure;
          float beach=runup.w*shoreK;
          float wetReach=runup.y*beach;
          if(depth < -0.02 && (beach<0.01||depth+wetReach<0.0))discard;
          vec3 surf=coastalSurf(vP2,depth,grade,uTime)*shoreK;
          vec4 rock=vec4(8.0,0.0,0.0,0.0);
          if(shoreK>0.001&&depth>-0.9&&depth<4.4)rock=rockResponse(vP2,gradient,max(surf.x,max(0.0,-depth)+0.06));
          surf=coastalSurf(vP2,depth-0.28*rock.w,grade,uTime)*shoreK*exposure;
          surf.xy*=vec2(1.0-0.75*rock.z,1.0-0.65*rock.z);
          vec3 N = normalize(vUp);
          vec2 rippleN = vec2(0.0); // wave slope for the mirror distortion (0 = calm far field)
          if (detail > 0.004) {
            vec3 nrm;
            seaGerstner(vP2, uTime, vAmp, nrm);
            nrm = normalize(vec3(nrm.xy * detail, nrm.z));
            rippleN = nrm.xy;
            N = normalize(uE * nrm.x + uN * nrm.y + N * nrm.z);
          }
          // The surf normal follows the displaced mesh itself. This also keeps
          // the angular crest faces coherent with the actual silhouette.
          vec3 faceN=inverseTransformDirection(normalize(cross(dFdx(vView),dFdy(vView))),viewMatrix);
          if(dot(faceN,vUp)<0.0)faceN=-faceN;
          N=normalize(mix(N,faceN,shoreK*smoothstep(0.015,0.12,surf.x*uAmp)));
          rippleN=vec2(dot(N-vUp,uE),dot(N-vUp,uN));
          vec3 V = inverseTransformDirection(normalize(-vView),viewMatrix);
          vec3 sky = uSkyColor;
          #ifdef USE_FOG
            sky = fogColor;
          #endif
          // Shallow strip: a few cm of water over a bright floor barely mirrors, and
          // reflecting the land there read as a wrong doubled shoreline. Answer that
          // by turning the REFLECTANCE down (so the shallows show their own colour),
          // NOT by leaving it at full strength over a fallback sky — the fog colour
          // is a distance haze, far brighter than the near sky, and mixing it in at
          // grazing angles laid a pale wash along every coast.
          float shallowK = smoothstep(0.12, 1.3, depth);
          float reflK = mix(0.3, 1.0, shallowK);
          // Real planar reflection (terrain / players / sky) replaces the flat sky
          // tint where available — projected to the mirror texture, rippled by the
          // wave slope. waterShade still Fresnel-gates it (grazing = mirror, down = body).
          if (uReflStrength > 0.0) {
            vec4 rc = vReflection;
            if (rc.w > 0.0) {
              vec2 ruv = rc.xy / rc.w + rippleN * uReflDistort;
              vec3 reflCol = texture2D(uReflTex, clamp(ruv, 0.0, 1.0)).rgb;
              // Compress blown-out highlights: in DAY the sun renders far brighter in
              // the mirror than in-sky (the atmosphere doesn't dim it there) and reads
              // as a glaring blob — so compress hard by day, but barely at night so the
              // moon / nebulae / colour splotches stay visible on the dark night sea.
              float lum = dot(reflCol, vec3(0.299, 0.587, 0.114));
              reflCol /= 1.0 + max(0.0, lum - 0.5) * (0.25 + 1.5 * uDayLight);
              // Fade to the sky outside the mirror frame (no clamp-smear of edge pixels).
              // The mirror is rendered with REFL_FOV_SCALE of headroom, so this band
              // sits OFF-screen — at scale 1 it framed the viewport in bright fog-sky,
              // a white border ~5% of the frame wide that crawled with the waves.
              vec2 fd = smoothstep(vec2(0.0), vec2(0.05), ruv) * smoothstep(vec2(0.0), vec2(0.05), 1.0 - ruv);
              float inB = fd.x * fd.y;
              inB *= shallowK; // no land in the mirror across the shoreline strip
              if (uReflDebug > 0.5) { gl_FragColor = vec4(mix(sky, reflCol, inB), 1.0); return; }
              sky = mix(sky, reflCol, uReflStrength * inB);
            }
          }
          // Thin broken crest rims, with sparse wash on their rear faces. There
          // is no stationary white outline and no foam in deep open water.
          float shadow=oceanCloudShadow(vWorld);
          gl_FragColor=waterShadeSurface(vWorld,N,V,depth,vEdge,sky,reflK,shadow,0.5,0.0);
          float faceLight=clamp(dot(N,uSunDir),0.0,1.0);
          gl_FragColor.rgb*=mix(1.0,0.8+0.32*faceLight,smoothstep(0.035,0.18,surf.x*uAmp)*uDayLight);
          float resolved=1.0-smoothstep(0.4,2.5,max(fw.x,fw.y));
          float foam=clamp(surf.y*0.84+surf.z,0.0,0.88)*resolved*smoothstep(0.0,0.3,uDayLight);
          if(shoreK>0.001)foam=max(foam,cliffContactFoam(vP2,depth,grade,uTime)
            *shoreK*exposure*resolved*smoothstep(0.0,0.3,uDayLight));
          // Thin advancing edge plus broken draining lace. These move over
          // dry sand; the darker transparent footprint persists behind them.
          float advance=depth+runup.x*beach;
          float cover=smoothstep(0.0,0.045,advance)*beach;
          float grain=sin(vP2.x*1.7+vP2.y*0.83)*sin(vP2.x*0.61-vP2.y*1.93);
          float rim=(1.0-smoothstep(0.018,0.065,abs(advance+0.012*grain)))*beach;
          rim*=smoothstep(-0.6,0.25,grain+0.35*sin(vP2.x*0.31-vP2.y*0.27));
          float lace=0.0;
          if(depth<0.9&&cover>0.01){
            vec2 seaward=gradient/max(grade,0.001);
            lace=(1.0-smoothstep(0.2,0.9,depth))*cover*smoothstep(0.15,0.5,runup.z)
              *(1.0-smoothstep(0.72,1.0,runup.z))*foamLace(vP2*2.0+seaward*runup.z*1.7)*0.3;
          }
          float contact=(1.0-smoothstep(0.04,0.65,max(0.0,rock.x)))*smoothstep(-0.22,0.03,rock.x);
          float rush=cover*(1.0-smoothstep(0.55,0.98,runup.z))*(1.0-smoothstep(0.2,1.0,depth));
          float impact=contact*(0.25+0.75*rock.y)*(surf.y+surf.x*1.5+0.7*rush);
          foam=max(foam,clamp(rim*0.8+lace+impact*0.85,0.0,0.88)*resolved*smoothstep(0.0,0.3,uDayLight));
          float filmK=(1.0-smoothstep(0.0,0.3,depth))*beach;
          float wet=smoothstep(0.0,0.12,depth+wetReach)*beach;
          // Sand remains visible through a centimetre film. A small neutral
          // darkening survives its retreat, then dries before the next set.
          vec3 filmColor=mix(vec3(0.08)*uDayLight,sky*0.5,cover*0.5);
          gl_FragColor.rgb=mix(gl_FragColor.rgb,filmColor,filmK);
          gl_FragColor.a=mix(gl_FragColor.a,wet*(0.09+0.12*cover),filmK);
          gl_FragColor.rgb=mix(gl_FragColor.rgb,uFoam*(0.16+0.84*uDayLight)*shadow,foam);
          gl_FragColor.a=max(gl_FragColor.a,foam*0.95);
          #include <fog_fragment>
        }
      `,
    });

    this.mesh = new THREE.Mesh(geo, this.mat);
    // Radial projection shortens the planar chord; 64 m covers wave/swash
    // displacement (the signed shore field itself is capped at 32 m). Keeping
    // this bound lets the 300 km depth slice reject all 131k patch triangles.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Math.SQRT2 * PATCH_SIZE / 2 + 64);
    this.mesh.renderOrder = 2; // over the far sea sphere, under the atmosphere shell
    this.mesh.visible = false;
  }

  /**
   * Re-seat the patch under the viewer and refresh wave + depth uniforms.
   * @param viewVisPos  viewer's INTERPOLATED render position (world space) — the
   *   same `lerp(prevPos, pos, alpha)` the avatar/ship draw with. Using the raw
   *   sim position makes the water judder against the interpolated sphere.
   * @param planet   the sea planet underfoot (or null → hide)
   * @param origin   floating-origin offset (render = world − origin)
   * @param time     wall-clock seconds (any monotonic source)
   * @param sunDir   unit direction TOWARD the sun, render space
   * @param sunColor star colour scaled by daylight
   * @param dayLight this side's day factor 0..1 (the unlit surface darkens with it)
   */
  update(
    viewVisPos: THREE.Vector3,
    planet: Planet | null,
    origin: THREE.Vector3,
    time: number,
    sunDir: THREE.Vector3,
    sunColor: THREE.Color,
    dayLight: number,
  ): void {
    const seaR = planet?.def.seaRadius ?? null;
    if (!planet || seaR === null || (planet.def.seaSurface !== undefined && planet.def.seaSurface !== 'water')) {
      this.mesh.visible = false;
      return;
    }
    // Altitude band cull: far above (or well below) the sea the 220 m patch is
    // pointless — the static far sphere carries the horizon.
    const alt = viewVisPos.distanceTo(planet.pose.center) - seaR;
    if (alt > SHOW_ALT_MAX || alt < SHOW_ALT_MIN) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    const u = this.mat.uniforms;

    // INTERPOLATED render transform — identical to what the far sphere (a child
    // of group) and the avatar draw with this frame.
    this.center.copy(planet.group.position); // render planet centre
    this.up.copy(viewVisPos).sub(origin).sub(this.center).normalize();
    const spin = planet.group.quaternion;
    this.invSpin.copy(spin).invert();
    // Snapshot the carry pose for the mirror re-aim (see renderReflection).
    this.poseNowPos.copy(this.center);
    this.poseNowQuat.copy(spin);

    // Planet-local direction, for the slowly re-anchored gnomonic reference.
    this.localDir.copy(this.up).applyQuaternion(this.invSpin);
    let reanchored = false;
    if (!this.anchored || this.localDir.dot(this.refLocal) < REANCHOR_DOT) {
      this.refLocal.copy(this.localDir);
      this.anchored = true;
      reanchored = true; // basis rotated → the depth grid footprint moved
    }
    // Tangent basis about the reference (planet-local), then rotate to render.
    const pole = Math.abs(this.refLocal.y) < 0.9 ? UP_Y : UP_X;
    this.eLocal.crossVectors(pole, this.refLocal).normalize();
    this.nLocal.crossVectors(this.refLocal, this.eLocal);
    this.refRen.copy(this.refLocal).applyQuaternion(spin);
    this.eRen.copy(this.eLocal).applyQuaternion(spin);
    this.nRen.copy(this.nLocal).applyQuaternion(spin);

    // Seat the grid tangent to the sphere at the viewer's sub-point.
    this.mesh.position.copy(this.center).addScaledVector(this.up, seaR);
    u.uPatchOrigin.value.copy(this.up).multiplyScalar(seaR);
    this.basis.makeBasis(this.eRen, this.nRen, this.up);
    this.mesh.quaternion.setFromRotationMatrix(this.basis);

    // Coastal depth field, baked on a lattice LOCKED TO THE PLANET. Rebaking the
    // 97² procedural-terrain grid every frame is the ocean's dominant CPU cost, so
    // it is cached — but the patch slides under the viewer continuously, and a cache
    // that recentres on the viewer drags the texel grid along. Bilinear
    // reconstruction from 25 m texels then returns a DIFFERENT depth for the same
    // piece of seabed each time the grid phase shifts, so the shoreline (and the
    // foam riding it) jumped every time the cache expired — a visible twitch every
    // second or two while running. Both the bake and the lookup are therefore in the
    // ANCHORED gnomonic frame (the wave plane's p2), and the centre is snapped to
    // whole texels of it: a rebake slides the window along a lattice that never
    // moves, so nothing already on screen changes value.
    const gk = seaR / Math.max(this.up.dot(this.refRen), 0.15); // p2 of the viewer
    const cx = gk * this.up.dot(this.eRen);
    const cy = gk * this.up.dot(this.nRen);
    const bx = Math.round(cx / DEPTH_TEXEL) * DEPTH_TEXEL;
    const by = Math.round(cy / DEPTH_TEXEL) * DEPTH_TEXEL;
    const cellChanged = bx !== this.bakeX || by !== this.bakeY;
    if (depthRebakeNeeded(this.depthBaked, reanchored, planet !== this.lastBakePlanet, cellChanged)) {
      const reuse = this.depthBaked && !reanchored && planet === this.lastBakePlanet;
      const dx = reuse ? (bx - this.bakeX) / DEPTH_TEXEL : Infinity;
      const dy = reuse ? (by - this.bakeY) / DEPTH_TEXEL : Infinity;
      this.bakeX = bx;
      this.bakeY = by;
      this.fillDepth(planet, seaR, dx, dy);
      this.depthBaked = true;
      this.lastBakePlanet = planet;
    }
    (u.uDepthCenter.value as THREE.Vector2).set(this.bakeX, this.bakeY);

    // Fine signed seabed around the viewer. The 2 m world lattice is fixed;
    // moving the 256 m window by 16 m preserves all overlapping depth samples.
    const shoreX=Math.round(cx/SHORE_CACHE_STEP)*SHORE_CACHE_STEP;
    const shoreY=Math.round(cy/SHORE_CACHE_STEP)*SHORE_CACHE_STEP;
    const shoreReset=reanchored||planet!==this.shorePlanet;
    if(shoreReset||shoreX!==this.shoreX||shoreY!==this.shoreY){
      const start=performance.now(),reset=reanchored||planet!==this.shorePlanet;
      const shiftX=reset?Infinity:(shoreX-this.shoreX)/SHORE_STEP;
      const shiftY=reset?Infinity:(shoreY-this.shoreY)/SHORE_STEP;
      const sampled=fillShoreWindow(this.shoreScratch,this.shoreData,shiftX,shiftY,(i,j,channel)=>{
        if(channel===1)return THREE.DataUtils.toHalfFloat(this.shoreSample.estuaryShelter??0);
        const x=shoreX-SHORE_SPAN/2+i*SHORE_STEP,y=shoreY-SHORE_SPAN/2+j*SHORE_STEP;
        this.node.copy(this.refLocal).multiplyScalar(seaR).addScaledVector(this.eLocal,x).addScaledVector(this.nLocal,y).normalize();
        planet.sampler.sample(this.node.x,this.node.y,this.node.z,this.shoreSample,false);
        const depth=seaR-(planet.def.radius+this.shoreSample.h*planet.sampler.amplitude);
        return THREE.DataUtils.toHalfFloat(Math.max(-32,Math.min(32,depth)));
      },2);
      this.shoreData.set(this.shoreScratch);
      this.shoreX=shoreX;this.shoreY=shoreY;this.shorePlanet=planet;
      Object.assign(this.shoreStats,{bakes:this.shoreStats.bakes+1,sampled,reused:this.shoreData.length/2-sampled,milliseconds:performance.now()-start});
      this.shoreTex.needsUpdate=true;
    }
    u.uShoreCenter.value.set(shoreX,shoreY);u.uShoreView.value.set(cx,cy);u.uShoreReady.value=1;
    this.shoreRocks.update(planet.shoreRocks,this.refLocal,this.eLocal,this.nLocal,seaR,shoreX,shoreY,shoreReset);
    u.uRockReady.value=1;

    // Per-planet colour triad (cheap; only when the sea colour changes).
    const sc = planet.def.seaColor;
    const key = `${sc[0]},${sc[1]},${sc[2]}`;
    if (key !== this.lastSeaKey) {
      this.lastSeaKey = key;
      const c = deriveWaterColors(sc);
      (u.uShallow.value as THREE.Vector3).copy(c.shallow);
      (u.uDeep.value as THREE.Vector3).copy(c.deep);
      (u.uFoam.value as THREE.Vector3).copy(c.foam);
    }

    (u.uTime.value as number) = time % TIME_WRAP;
    (u.uCenter.value as THREE.Vector3).copy(this.center);
    u.uSeaR.value = seaR;
    (u.uRefDir.value as THREE.Vector3).copy(this.refRen);
    (u.uE.value as THREE.Vector3).copy(this.eRen);
    (u.uN.value as THREE.Vector3).copy(this.nRen);
    (u.uSunDir.value as THREE.Vector3).copy(sunDir);
    (u.uSunColor.value as THREE.Vector3).set(sunColor.r, sunColor.g, sunColor.b);
    u.uDayLight.value = dayLight;

    // Cloud shadows from the sea planet's low deck. The water is in WORLD space, so the
    // shader folds it into the planet-local frame (uCloudInvRot) where the coverage RT
    // lives; the sun goes local the same way. str 0 ⇒ airless world, no shadow.
    const cs = planet.getCloudShadow();
    if (cs && cs.str > 0) {
      u.uCloudCover.value = cs.tex;
      u.uCloudFrame.value = cs.frame;
      u.uCloudR.value = cs.radius;
      u.uCloudShadowStr.value = cs.str;
      this.cloudM4.makeRotationFromQuaternion(this.invSpin);
      (u.uCloudInvRot.value as THREE.Matrix3).setFromMatrix4(this.cloudM4);
      (u.uCloudSun.value as THREE.Vector3).copy(sunDir).applyQuaternion(this.invSpin);
    } else {
      u.uCloudShadowStr.value = 0;
    }
  }

  /** Resize the reflection target on canvas resize. Half-res of the canvas is plenty. */
  setReflectionSize(width: number, height: number): void {
    this.reflection.setSize(Math.round(width), Math.round(height));
  }

  /** Release the sea mirror while an inland river owns this frame. */
  clearReflection(): void {
    this.mat.uniforms.uReflStrength.value = 0;
    this.mirrorFresh = false;
  }

  /** The sea already owns the mirror at a river mouth. Reuse that texture
   * and plane for the adjoining river, within its normal plane/range gates. */
  shareRiverReflection(material: THREE.ShaderMaterial | null, camera: THREE.PerspectiveCamera): void {
    if(!material||this.mat.uniforms.uReflStrength.value<=0)return;
    const u=material.uniforms,o=this.mat.uniforms;
    u.uRiverReflTex.value=o.uReflTex.value;
    u.uRiverReflMatrix.value.copy(o.uReflMatrix.value);
    u.uRiverReflViewMatrix.value.copy(o.uReflViewMatrix.value);
    u.uRiverReflAnchor.value.copy(this.mesh.position);
    u.uRiverReflViewAnchor.value.copy(this.mesh.position).applyMatrix4(camera.matrixWorldInverse);
    this.riverPlane.setFromNormalAndCoplanarPoint(this.up,this.mesh.position);
    u.uRiverReflPlane.value.set(this.up.x,this.up.y,this.up.z,this.riverPlane.constant);
    this.riverPlane.applyMatrix4(camera.matrixWorldInverse);
    u.uRiverReflViewPlane.value.set(this.riverPlane.normal.x,this.riverPlane.normal.y,this.riverPlane.normal.z,this.riverPlane.constant);
    u.uRiverReflStrength.value=o.uReflStrength.value;
  }

  /**
   * Render the scene reflected in the water into the reflection target, then point
   * the shader at it. Call ONCE per frame AFTER update() (which seats the plane)
   * and AFTER every scene object is in its final pose, but BEFORE the main
   * renderer.render(). No-op (reflection off → fog-sky fallback) when the patch is
   * hidden or the camera is below the surface.
   *
   * @param hide  extra water surfaces to suppress for the pass (the planet's far
   *   sea sphere). The patch hides itself; without this the mirror sees the water
   *   and feeds back on itself.
   */
  renderReflection(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    hide: readonly THREE.Object3D[],
    clearColor: THREE.Color,
  ): void {
    if (!this.mesh.visible) {
      this.mat.uniforms.uReflStrength.value = 0;
      this.mirrorFresh = false;
      return;
    }
    // Half-rate refresh: keep last frame's texture, and RE-AIM its matrix at the
    // world's current pose. The stale texture+matrix pair is only self-consistent
    // while render space holds still — but the planet orbits and spins on the wall
    // clock, translating every render-space coordinate (terrain, water, camera —
    // it carries them all) a little each frame. Left alone, the stale matrix
    // projects from the OLD poses, so the reflection lands one frame of orbital
    // motion off on every reuse frame: a 30 Hz back-and-forth jitter. Nearly all
    // reflected content is planet-carried, so composing the matrix with the
    // planet's motion delta (current render coords → mirror-time render coords)
    // pins it: a planet-fixed point samples the SAME texel on fresh and reuse
    // frames, and only genuinely independent movers (other players) lag a frame.
    // A big camera jump (teleport, warp, floating-origin rebase) still forces a
    // full refresh — a rebase is not planet motion and the delta can't express it.
    this.camNow.setFromMatrixPosition(camera.matrixWorld);
    const jumped = this.camNow.distanceToSquared(this.lastReflCam) > this.reflJumpM * this.reflJumpM;
    this.lastReflCam.copy(this.camNow);
    // Parallax gate: how far has the EYE moved in the planet's own frame since the
    // standing mirror was rendered? (Planet-local, so orbital carry cancels.)
    this.spinInvNow.copy(this.poseNowQuat).invert();
    this.eyeLocalNow.copy(this.camNow).sub(this.poseNowPos).applyQuaternion(this.spinInvNow);
    const eyeStill =
      this.eyeLocalNow.distanceToSquared(this.mirrorEyeLocal) < this.reflEyeMoveM * this.reflEyeMoveM;
    this.reflFrame++;
    const u = this.mat.uniforms;
    if (
      this.mirrorFresh &&
      !jumped &&
      eyeStill &&
      u.uReflStrength.value > 0 &&
      this.reflFrame % Math.max(1, this.reflInterval | 0) !== 0
    ) {
      this.reaimA.makeRotationFromQuaternion(this.mirrorQuat).setPosition(this.mirrorPos);
      this.reaimB.makeRotationFromQuaternion(this.poseNowQuat).setPosition(this.poseNowPos).invert();
      this.reaimA.multiply(this.reaimB); // now-render → planet-local → mirror-time-render
      (u.uReflMatrix.value as THREE.Matrix4)
        .copy(this.reflection.textureMatrix)
        .multiply(this.reaimA);
      u.uReflViewMatrix.value.multiplyMatrices(u.uReflMatrix.value,camera.matrixWorld);
      return; // reuse the standing mirror — the whole scene pass is skipped
    }
    this.hidden.length = 0;
    this.hiddenVis.length = 0;
    this.mesh.visible = false;
    for (const o of hide) {
      this.hidden.push(o);
      this.hiddenVis.push(o.visible);
      o.visible = false;
    }
    let ok = false;
    try {
      ok = this.reflection.render(
        renderer,
        scene,
        camera,
        this.mesh.position,
        this.up,
        clearColor,
        REFL_FOV_SCALE,
      );
    } finally {
      this.mesh.visible = true;
      for (let i = 0; i < this.hidden.length; i++) this.hidden[i].visible = this.hiddenVis[i];
      if (!ok) { this.mirrorFresh = false; u.uReflStrength.value = 0; }
    }
    u.uReflStrength.value = ok ? REFL_STRENGTH : 0;
    this.mirrorFresh = ok; // a failed pass (underwater) retries every frame until it lands
    if (ok) {
      // Fresh mirror: the probe matrix applies as-is, and this frame's carry pose
      // becomes the reference the reuse frames re-aim against.
      (u.uReflMatrix.value as THREE.Matrix4).copy(this.reflection.textureMatrix);
      this.mirrorPos.copy(this.poseNowPos);
      this.mirrorQuat.copy(this.poseNowQuat);
      this.mirrorEyeLocal.copy(this.eyeLocalNow);
      u.uReflViewMatrix.value.multiplyMatrices(u.uReflMatrix.value,camera.matrixWorld);
    }
  }

  /**
   * Sample the analytic terrain floor under the grid footprint into the depth
   * texture. Grid coords are the ANCHORED gnomonic ones the shader looks depth up
   * with — `normalize(ref·seaR + e·X + n·Y)` is the exact inverse of
   * `p2 = seaR·(d·e, d·n)/(d·ref)` — centred on the snapped lattice cell rather
   * than on the viewer, which is what makes consecutive bakes agree. Coarse
   * (DEPTH_N²) and linear-filtered: depth colour is low-frequency, so this stays a
   * few-k surface samples, and only on a cell crossing.
   */
  private fillDepth(planet: Planet, seaR: number, dx: number, dy: number): void {
    const start = performance.now();
    const sampler = planet.sampler;
    const sampled = fillDepthWindow(this.depthScratch, this.depthData, DEPTH_N, dx, dy, (i, j) => {
      const ly = this.bakeY + (j / DEPTH_RES - 0.5) * PATCH_SIZE;
      const lx = this.bakeX + (i / DEPTH_RES - 0.5) * PATCH_SIZE;
      // Work directly in planet-local coordinates. Orbital carry/rebases
      // cannot change samples retained on the fixed lattice.
      this.node
        .copy(this.refLocal)
        .multiplyScalar(seaR)
        .addScaledVector(this.eLocal, lx)
        .addScaledVector(this.nLocal, ly)
        .normalize();
      sampler.sample(this.node.x,this.node.y,this.node.z,this.shoreSample,false);
      const floorR = planet.def.radius+this.shoreSample.h*sampler.amplitude;
      const depth = seaR - floorR; // <0 over land (clamped to 0)
      // SQRT-encoded: eight bits spread linearly over 24 m put a 9 cm step on a
      // 65 cm-wide foam ramp, so the shoreline banded into half a dozen visible
      // contours. Storing √(d/max) spends most of the range on the first metre —
      // ~3 cm resolution there — and the shader squares it back.
      const r = Math.sqrt(Math.max(0, Math.min(1, depth / DEPTH_STORE_MAX)));
      return (r * 255) | 0;
    },()=>Math.round((this.shoreSample.estuaryShelter??0)*255));
    this.depthData.set(this.depthScratch);
    Object.assign(this.depthStats, { bakes: this.depthStats.bakes + 1, sampled,
      reused: DEPTH_N * DEPTH_N - sampled, milliseconds: performance.now() - start });
    this.depthTex.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mat.dispose();
    this.depthTex.dispose();
    this.shoreTex.dispose();
    this.shoreRocks.dispose();
    this.reflection.dispose();
  }
}
